import actionTypes, * as actionObjects from "projects-new/list/action-types"

import * as listActionCreators from "shared/profiles/listAdder/action-creators"
import * as djangioActionCreators from "shared/djangio/action-creators"
import * as outboxActions from "outbox/action-creators"
import { segueToSearch, postSavedSearch } from "search-new/action-creators"
import { clearSavedSearch } from "alerts/action-creators"
import { ALERT_FORM_KEY } from "shared/imports/sharedConstants"
import { segue, destroyReduxForm } from "global-action-creators"
import { createNewTemplateFromSearch } from "search-new/containers/Downloads/new-download-action-creators"
import { toggleIsSubmitting, postSheet } from "app/static/frontend/sheets/sheet-action-creators"
import BACKENDERROR from "app/static/frontend/imports/backenderror"
import { swalContents } from "sheets/components/helpTexts"
import swal from "sweetalert"
import { MODEL_NAME_RECEIPT, MODEL_NAME_DISBURSEMENT, transformMoneyballRequest } from "shared/djangio/moneyballHelpers"

// All params are named params being passed via an obj as the first argument

/**
 * Grabs items for the list. Applies filters (both permanent and based on the search bar, including offset).
 * @name getListItems
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {obj} - filters - all the filters that should be used to get items for this list, including offset
 * @param {number} - dataType - quorumDataType for the list
 * @param {number} - id - id of the list, used for redux store purposes
 * @param {number} - offset - used to determine whether we are appending or a fresh set of items, also grabs the right page
 */
export const getListItems = ({ filters, dataType, id, offset }) => {
    const finalFilters = {
        count: true,
        ...filters,
    }

    return djangioActionCreators.resourceGetList(
        djangioActionCreators.getResourceForDataType(dataType),
        // if an offset is passed, this is not the initial fetch for listItems (getMore action appends, get replaces)
        offset ? actionObjects.getMoreListItems : actionObjects.getListItems,
        finalFilters,
        { id, offset },
        false,
    )
}

/**
 * Go into outbox with this list items as recipients
 * @name emailList
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {obj} - permanentFilters - the filters used to get all the items for a list
 * @param {number} - dataType - quorumDataType for the list
 * @param {string} - name - the name of the list
 */
export const emailList =
    ({ permanentFilters, dataType, name }) =>
    async (dispatch) => {
        swal({
            title: "Generating Email",
            text: "Just a moment...",
            icon: "info",
            buttons: {},
            closeOnClickOutside: false,
        })
        const filters = permanentFilters
        if (filters.filters) {
            Object.assign(filters, filters.filters)
            delete filters.filters
        }
        const email = await dispatch(outboxActions.createBulkEmail())
        // we wanna use the nice built-in outbox functionality, so we are faking the filters here as
        // a list (regardless of whether they are attached to an actual newList or not)
        const fakeList = { filters, name, list_type: dataType }
        const selectionResult = await dispatch(outboxActions.addListAsSelection(fakeList))
        await dispatch(outboxActions.segueToOutbox({ bulkEmailId: email.id }))
        swal.close()
        return selectionResult
    }

/**
 * Go into outbox with this list items as recipients
 * @name abEmailList
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {obj} - permanentFilters - the filters used to get all the items for a list
 * @param {number} - dataType - quorumDataType for the list
 * @param {string} - name - the name of the list
 */
export const abEmailList =
    ({ permanentFilters, dataType, name }) =>
    async (dispatch) => {
        swal({
            title: "Generating Email",
            text: "Just a moment...",
            icon: "info",
            buttons: {},
            closeOnClickOutside: false,
        })
        const filters = permanentFilters
        if (filters.filters) {
            Object.assign(filters, filters.filters)
            delete filters.filters
        }
        const email = await dispatch(outboxActions.createBulkEmail({ is_abtest: true }))
        // we wanna use the nice built-in outbox functionality, so we are faking the filters here as
        // a list (regardless of whether they are attached to an actual newList or not)
        const fakeList = { filters, name, list_type: dataType }
        const selectionResult = await dispatch(outboxActions.addListAsSelection(fakeList))
        await dispatch(outboxActions.segueToOutbox({ bulkEmailId: email.id }))
        swal.close()
        return selectionResult
    }

/**
 * Downloads the list as a CSV
 * @name downloadCSV
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {obj} - permanentFilters - the filters used to get all the items for a list
 * @param {number} - dataType - quorumDataType for the list
 */
export const downloadCSV = ({ permanentFilters, dataType }) => {
    const model = djangioActionCreators.getResourceForDataType(dataType)
    const filters = [MODEL_NAME_RECEIPT, MODEL_NAME_DISBURSEMENT].includes(model.__name__)
        ? transformMoneyballRequest(permanentFilters, model)
        : permanentFilters

    return djangioActionCreators.downloadFile({
        resource: model.objects.filter(filters),
        actionTypes: actionObjects.downloadList,
        requestArgs: { format: "csv" },
    })
}

/**
 * Sends a request to create a pdf download of the list to be emailed
 * @name downloadPDF
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {obj} - permanentFilters - the filters used to get all the items for a list
 * @param {number} - dataType - quorumDataType for the list
 * @param {string} - name - the name of the list
 * @param {string} - totalCount - count of the searchify set
 */
export const downloadPDF = ({ permanentFilters, dataType, name, totalCount }) => {
    const filters = permanentFilters || {}
    return createNewTemplateFromSearch({
        filterQdt: dataType,
        filters,
        filterElementCount: totalCount,
        templateName: name,
    })
}

/**
 * call deleteItem action for oldList, then addItem action for newList!
 * @name moveListItem
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {obj} - item - used to call the add/remove actions with the right id, also for adding/removing in the store
 * @param {number} - oldListId - the list we are removing an item from
 * @param {number} - newListId - the list we are moving an item to
 */
export const moveListItem =
    ({ oldListId, newListId, item }) =>
    async (dispatch) => {
        dispatch(listActionCreators.removeListItem({ item, list: { id: oldListId } }))
        dispatch(listActionCreators.addListItem({ item, list: { id: newListId } }))
    }

/**
 * Sends the user to search with the filters required to give the contents of the list as search results
 * @name showInSearch
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {obj} - filters - the filters used to get all the items for a list
 * @param {number} - dataType - quorumDataType for the list
 */
export const showInSearch = ({ filters, dataType }) => {
    //flatten filters
    if (filters.filters) {
        Object.assign(filters, filters.filters)
        delete filters.filters
    }
    return segueToSearch({ quorumDataType: dataType, initialFilter: filters })
}

/**
 * Creates a saved search, then segues to alert based on that search.
 * This is only used if a createAlert is not passed to List (in case user of list want custom ss logic)
 * @name createAlertDefault
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {obj} - permanentFilters - the filters used to get all the items for a list
 * @param {number} - dataType - quorumDataType for the list
 */
export const createAlertDefault =
    ({ permanentFilters, dataType, name }) =>
    async (dispatch) => {
        await dispatch(clearSavedSearch())
        await dispatch(destroyReduxForm(ALERT_FORM_KEY))
        const { added_ids, deleted_ids, ...filters } = permanentFilters

        const savedSearch = await dispatch(
            postSavedSearch({
                added_ids,
                deleted_ids,
                filters: [filters],
                name: `Alert for ${name}`,
                data_type: dataType,
                user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
            }),
        )
        const encodedName = encodeURIComponent(name)
        const url = `/alert/search/${savedSearch.id}/list/?name=${encodedName}`
        return dispatch(segue(url))
    }

/**
 * Creates a new sheet from a List (issue management, tracking dashboard)
 * First, a safedSearch is created to be able to contruct the rows for the new sheet.
 * @name createSheetFromList
 * @function
 * @param {number} - dataType - quorumDataType for the list
 * @param {number or string} - id of the list (number if Issues List. string if TB list)
 * @param {obj} - filters - the filters used to get all the items for the list
 */
export const createSheetFromList =
    ({ dataType, id, name, filtersForSearch, filters }) =>
    async (dispatch) => {
        dispatch(toggleIsSubmitting(true))
        const { added_ids, deleted_ids, ...searchFilters } = filtersForSearch
        const savedSearchKwargs = {
            added_ids,
            deleted_ids,
            user: DjangIO.app.userdata.models.QuorumUser.foreignKey(parseInt(Userdata.id)),
            data_type: dataType,
            name: `savedSearch-list-${name}`,
            filters:
                typeof id === "string" // tracking board filters vs list with id
                    ? [filters]
                    : [
                          {
                              for_searchify_filters: {
                                  filters: [{ ...searchFilters["filters"], ...filters }],
                                  qdt: DjangIO.app.models.QuorumDataType.by_value(dataType).value,
                              },
                          },
                      ],
            created_regions: Userdata.current_regions_values,
            from_searchify: true,
        }

        try {
            const savedSearch = await dispatch(postSavedSearch(savedSearchKwargs))

            const sheet = await dispatch(
                postSheet({
                    creatorsName: Userdata.name,
                    data_type: dataType,
                    is_limited_to_creator: false,
                    is_public: false,
                    locked: false,
                    name: name,
                    organization: DjangIO.app.userdata.models.Organization.resourceUriFromId(Userdata.organization_id),
                    rows: DjangIO.app.userdata.models.SafedSearch.foreignKey(savedSearch.id),
                    show_map: false,
                    user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
                }),
            )
            dispatch(toggleIsSubmitting(false))

            return dispatch(segue(`/sheet/${sheet.id}/`))
        } catch (error) {
            dispatch(toggleIsSubmitting(false))
            await BACKENDERROR(error, swalContents.failCreateSheet)
        }
    }

// @function resetLoadingState, takes in list ID, resets redux state for that list
export const resetLoadingState = ({ id }) => ({ id, type: actionTypes.resetLoadingState })
// @function setListState, takes in list id, attr, value, and sets that attr with value in the redux store
// (useful to have all the subcomponents of newList talking to the store at once)
export const setListState = ({ id, attr, value }) => ({ id, attr, value, type: actionTypes.setListState })
// @function initializeList, just shoves any arg as the initial state for that arg.id list in store
export const initializeList = (args) => Object.assign({ type: actionTypes.initializeList }, args)

// @function setListsForRefresh, no arguments, just marks all lists as needing to be refreshed
export const setListsForRefresh = () => ({ type: actionTypes.setListsForRefresh })
